import { Link, Navigate, useParams } from "react-router";
import { paths } from "@shared-core/routing/routes";
import { useCustomerDataAdapter } from "@shared-core/data/CustomerDataAdapterContext";
import { useListQuery } from "@shared-core/domain/useListQuery";
import { usePortalViewer } from "./PortalViewerContext";
import { Card } from "../../app/components/primitives/card";
import { Button } from "../../app/components/primitives/button";
import { AlertTriangle } from "lucide-react";

// `/orders/:orderNo/lines` was a many-item order's page of lines, one detail page per line (doc 48 retired it:
// the order page now lists the items beside their detail). The address still works, because the Orders list
// links a many-item row to it and customers have it bookmarked: it asks which item the order starts with and goes
// there, in place. One read of the order's lines, which is all the redirect needs; the order page makes its own.

export function PortalOrderLinesRedirect() {
  const { orderNo } = useParams<{ orderNo: string }>();
  const adapter = useCustomerDataAdapter();
  const { viewKey, buildPortalUrl } = usePortalViewer();

  const { state, retry } = useListQuery(
    () => adapter.getOrderLines(orderNo ?? ""),
    [adapter, viewKey, orderNo],
  );

  if (state.status === "loading") {
    return (
      <Card
        aria-busy="true"
        className="p-8 text-center text-sm text-muted-foreground"
      >
        Loading order…
      </Card>
    );
  }

  if (state.status === "error") {
    return (
      <Card className="space-y-3 p-8 text-center">
        <AlertTriangle className="mx-auto size-6 text-muted-foreground" />
        <p className="font-medium">We couldn&apos;t load this order</p>
        <p className="text-sm text-muted-foreground">{state.message}</p>
        <Button variant="outline" onClick={retry}>
          Try again
        </Button>
      </Card>
    );
  }

  const first = state.data?.lines[0];
  if (first) {
    return (
      <Navigate
        replace
        to={buildPortalUrl(
          paths.portalOrderDetail({ orderId: first.order_ref }),
        )}
      />
    );
  }

  return (
    <Card className="space-y-3 p-8 text-center">
      <p className="font-medium">We can&apos;t show you this order</p>
      <p className="text-sm text-muted-foreground">
        It either doesn&apos;t exist, or it isn&apos;t one of the orders
        you&apos;re set up to see. Your group administrator can change what you
        have access to.
      </p>
      <div>
        <Button asChild variant="outline">
          <Link to={buildPortalUrl(paths.portalOrders())}>Back to orders</Link>
        </Button>
      </div>
    </Card>
  );
}
